<template>
  <v-file-input
    v-model="localModel"
    :accept="availableExtensionsString"
    :class="bindClass(schema) + requiredInputClass"
    :hint="availableExt.length > 0 ? t('availableExtRule', { ext: availableExtensionsString }) : ''"
    :label="label"
    :persistent-hint="availableExt.length > 0"
    :rules="activeRules"
    chips
    prepend-icon=""
    prepend-inner-icon="mdi-file"
    v-bind="fieldProps"
    @change="updateFileByAPI()"
    @drop.prevent="updateFileByAPI()"
    @click:clear="removeFile(localModel, true)"
  >
    <template #selection="{ fileNames }">
      <v-chip
        class="pa-0 ma-0 py-1 px-2"
        closable
        @click:close="removeFile(localModel, true)"
      >
        <div
          class="cursor-pointer"
          @click.stop="getDownloadLink"
        >
          {{ fileNames[0] }}
        </div>
      </v-chip>
    </template>
  </v-file-input>
</template>

<script lang="ts" setup>
import { useEventBus } from '@vueuse/core';
import axios from 'axios';

import { computed, onMounted, ref, toRef } from 'vue';

import {
  useClass,
  useFormModel,
  useLabel,
  useLocale,
  useProps,
  useRules,
} from '@/core/composables';
import { useActiveRules } from '@/core/composables/useActiveRules';
import { toast } from '@/main';
import { EngineFileField } from '@/types/engine/controls';

interface FileInfo extends File {
  id: string;
  name: string;
  size: number;
  type: string;
}

const { schema, model, validationsDisabled } = defineProps<{
  schema: EngineFileField;
  model: object;
  validationsDisabled: boolean;
}>();

const { t } = useLocale();
const { bindClass } = useClass();
const { bindRules, rules, requiredInputClass } = useRules();
const { bindProps, fieldProps } = useProps();
const { getValue, setValue } = useFormModel();
const { label, bindLabel } = useLabel(schema);
const { activeRules } = useActiveRules({
  fieldProps,
  validationsDisabled: toRef(() => validationsDisabled),
  rules,
});

const maxFileSize = ref(schema.fileMaxSize || null);
const availableExt = ref(
  schema.fileAvailableExtensions?.trim().toLowerCase().split(',') ||
    schema.options?.context?.fileAvailableExtensions?.trim().toLowerCase().split(',') ||
    [],
);

const availableExtensionsString = computed(() =>
  availableExt.value
    .map((ext: string) => ext.trim().toLowerCase())
    .filter(Boolean)
    .map((ext: string) => (ext.startsWith('.') ? ext : `.${ext}`))
    .join(','),
);

const id = computed(() => {
  const params = new URLSearchParams(window.location.search);
  return (
    params.get('dataId') ||
    params.get('taskId') ||
    params.get('processId') ||
    params.get(idReference.value)
  );
});

const fileLabel = ref(schema.fileLabel || null);

const actionHandlerEventBus = useEventBus<string>('form-action');

const uploadFileUrl = ref(
  schema.url ||
    `/api/v1/features/{menuFeatureId}/files?dataId={dataId}&temporary=false&filePath=${schema.key}`,
);
const deleteFileUrl = ref(`/api/v1/features/{menuFeatureId}/files/{fileId}?dataId={dataId}`);

const idReference = ref(schema.idQueryParamName || 'id');

const featureId = computed(() => {
  return schema.options?.context?.menuFeatureId || 'unknown-feature-id';
});

const localModel = computed({
  get(): FileInfo {
    return getValue(model, schema);
  },
  set(val: any) {
    setValue(val, schema);
  },
});

const lastLocalModel = ref<FileInfo | null>(null);

function refreshAttachments() {
  actionHandlerEventBus.emit('form-action', { code: 'refresh-attachments' });
}

async function updateFileByAPI() {
  try {
    if (lastLocalModel.value) {
      await removeFile(lastLocalModel.value);
    }
    const formData = new FormData();
    formData.append('file', localModel.value);
    const response = await axios.post(
      uploadFileUrl.value
        .replace(`{menuFeatureId}`, featureId.value + '')
        .replace('{dataId}', id.value + ''),
      formData,
    );
    localModel.value = {
      id: response.data.fileId,
      name: localModel.value.name,
      size: localModel.value.size,
      type: localModel.value.type,
    };
    lastLocalModel.value = { ...localModel.value };
    if (fileLabel.value) {
      await addFileLabel(localModel.value);
    }
    refreshAttachments();
  } catch (error) {
    console.error('Error uploading file:', error);
    if (toast != null) {
      // @ts-ignore
      toast.error(error.message);
    }
    localModel.value = null;
  }
}

async function addFileLabel(file: FileInfo) {
  try {
    await axios.post(
      deleteFileUrl.value
        .replace('{menuFeatureId}', featureId.value + '')
        .replace(`{fileId}`, file.id + '')
        .replace('{dataId}', id.value + ''),
      {
        labels: [fileLabel.value],
        description: null,
      },
    );
  } catch (e) {
    console.error('Sth went wrong while adding file label');
  }
}

async function removeFile(file: FileInfo, cleanModel = false) {
  try {
    await axios.delete(
      deleteFileUrl.value
        .replace('{menuFeatureId}', featureId.value + '')
        .replace(`{fileId}`, file.id + '')
        .replace('{dataId}', id.value + ''),
    );
    lastLocalModel.value = null;
    if (cleanModel) {
      localModel.value = null;
    }
    refreshAttachments();
  } catch (error) {
    console.error('Error deleting file:', error);
    if (toast != null) {
      // @ts-ignore
      toast.error(error.message);
    }
  }
}

async function getDownloadLink() {
  try {
    const [baseUrl, queryString] = uploadFileUrl.value
      .replace(`{menuFeatureId}`, featureId.value + '')
      .replace('{dataId}', String(id.value))
      .split('?');
    const downloadUrl = `${baseUrl}/${localModel.value.id}/content${queryString ? '?' + queryString : ''}`;

    const response = await axios.get(downloadUrl, { responseType: 'blob' });

    const type = localModel.value?.type || response.data.type || 'application/octet-stream';
    const filename = localModel.value?.name || 'download';

    const blob = new Blob([response.data], { type });
    const url = URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Download failed:', error);
    if (toast != null) {
      // @ts-ignore
      toast.error(message);
    }
  }
}

function mapAdditionalRules() {
  if (maxFileSize.value != null) {
    const maxBytes = maxFileSize.value * 1024 * 1024;
    const fileSizeRule = (v: File | null | undefined) => {
      if (!v) return true;
      if (v.size > maxBytes) {
        return t('maxSizeFileRule', { maxSize: formatSize(maxBytes) });
      }
      return true;
    };
    rules.value = [...rules.value, fileSizeRule];
  }

  if (availableExt.value.length > 0) {
    const availableExtRule = (v: File | null | undefined) => {
      if (!v) return true;
      const ext = v.name.split('.').pop()?.toLowerCase();
      const allowed = availableExt.value.map((e: string) => e.trim().toLowerCase());
      if (ext && !allowed.includes(ext)) {
        return t('availableExtRule', { ext: availableExt.value.join(', ') });
      }
      return true;
    };
    rules.value = [...rules.value, availableExtRule];
  }
}

function formatSize(size: number | null): string {
  if (!size) return '';
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${(size / 1024).toFixed(1)} KB`;
}

onMounted(async () => {
  if (localModel.value && Object.keys(localModel.value).length === 0) {
    localModel.value = null;
    lastLocalModel.value = null;
  }

  if (localModel.value) {
    lastLocalModel.value = localModel.value;
  }
  await bindLabel(schema);
  await bindRules(schema);
  mapAdditionalRules();
  await bindProps(schema);
});
</script>

<style lang="css" scoped></style>

<i18n lang="json">
{
  "pl": {
    "maxSizeFileRule": "Plik przekracza maksymalny rozmiar {maxSize}",
    "availableExtRule": "Dozwolone rozszerzenia: {ext}"
  },
  "en": {
    "maxSizeFileRule": "File exceeds the maximum size {maxSize}",
    "availableExtRule": "Available extensions: {ext}"
  }
}
</i18n>
